import type { ClientConfig } from '@/types/client-config'

type ValidationResult =
  | { ok: true; config: ClientConfig }
  | { ok: false; error: string }

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function nonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0
}

export function validateConfig(raw: unknown): ValidationResult {
  if (!isObject(raw)) {
    return { ok: false, error: 'Config must be a JSON object' }
  }

  for (const key of ['branding', 'features', 'links', 'locale'] as const) {
    if (!isObject(raw[key])) {
      return { ok: false, error: `Missing required field: ${key} (must be an object)` }
    }
  }

  if (!Array.isArray(raw.subjects)) {
    return { ok: false, error: 'Missing required field: subjects (must be an array)' }
  }

  if (raw.subjects.length === 0) {
    return { ok: false, error: 'subjects must contain at least one entry' }
  }

  for (let i = 0; i < raw.subjects.length; i++) {
    const entry = raw.subjects[i]
    if (!isObject(entry)) {
      return { ok: false, error: `subjects[${i}] must be an object with key and label` }
    }
    if (!nonEmptyString(entry.key)) {
      return { ok: false, error: `subjects[${i}].key must be a non-empty string` }
    }
    if (!nonEmptyString(entry.label)) {
      return { ok: false, error: `subjects[${i}].label must be a non-empty string` }
    }
  }

  const branding = raw.branding as Record<string, unknown>
  for (const field of ['appName', 'logoPath', 'faviconPath', 'copyrightHolder'] as const) {
    if (!nonEmptyString(branding[field])) {
      return { ok: false, error: `Missing required field: branding.${field} (must be a non-empty string)` }
    }
  }

  const features = raw.features as Record<string, unknown>
  for (const flag of ['overview', 'createTest', 'previousTests', 'performance', 'settings', 'performancePercentile', 'highlights'] as const) {
    if (typeof features[flag] === 'undefined') {
      console.warn(`[config] Missing features.${flag} — defaulting to false`)
      features[flag] = false
    } else if (typeof features[flag] !== 'boolean') {
      return { ok: false, error: `Invalid field: features.${flag} (must be a boolean)` }
    }
  }

  for (const securityFlag of ['watermark', 'copyProtection', 'printProtection'] as const) {
    if (typeof features[securityFlag] === 'undefined') {
      features[securityFlag] = false
    } else if (typeof features[securityFlag] !== 'boolean') {
      return { ok: false, error: `Invalid field: features.${securityFlag} (must be a boolean)` }
    }
  }

  const links = raw.links as Record<string, unknown>
  for (const field of ['website', 'privacyPolicy', 'termsOfService', 'support'] as const) {
    if (typeof links[field] !== 'string' && links[field] !== null) {
      return { ok: false, error: `Invalid field: links.${field} (must be a string or null)` }
    }
  }

  const locale = raw.locale as Record<string, unknown>
  if (!nonEmptyString(locale.default)) {
    return { ok: false, error: 'Missing required field: locale.default (must be a non-empty string)' }
  }

  const validFormats = ['name', 'email', 'name-email']
  if (raw.watermarkConfig !== undefined) {
    if (!isObject(raw.watermarkConfig)) {
      return { ok: false, error: 'Invalid field: watermarkConfig (must be an object)' }
    }
    const wc = raw.watermarkConfig as Record<string, unknown>
    if (wc.format !== undefined) {
      if (typeof wc.format !== 'string' || !validFormats.includes(wc.format)) {
        return { ok: false, error: `Invalid field: watermarkConfig.format (must be one of: ${validFormats.join(', ')})` }
      }
    }
    if (wc.opacity !== undefined) {
      if (typeof wc.opacity !== 'number' || wc.opacity < 0 || wc.opacity > 1) {
        return { ok: false, error: 'Invalid field: watermarkConfig.opacity (must be a number between 0 and 1)' }
      }
    }
    if (wc.spacing !== undefined) {
      if (typeof wc.spacing !== 'number' || wc.spacing < 0) {
        return { ok: false, error: 'Invalid field: watermarkConfig.spacing (must be a non-negative number)' }
      }
    }
    if (wc.fontSize !== undefined) {
      if (!nonEmptyString(wc.fontSize)) {
        return { ok: false, error: 'Invalid field: watermarkConfig.fontSize (must be a non-empty string)' }
      }
    }
  }

  if (raw.highlightsConfig !== undefined) {
    if (!isObject(raw.highlightsConfig)) {
      return { ok: false, error: 'Invalid field: highlightsConfig (must be an object)' }
    }
    const hc = raw.highlightsConfig as Record<string, unknown>
    if (hc.maxPerQuestion !== undefined) {
      if (
        typeof hc.maxPerQuestion !== 'number' ||
        !Number.isInteger(hc.maxPerQuestion) ||
        hc.maxPerQuestion <= 0
      ) {
        console.warn('[config] highlightsConfig.maxPerQuestion must be a positive integer — defaulting to 20')
        hc.maxPerQuestion = 20
      }
    }
  } else {
    console.warn('[config] highlightsConfig missing — defaulting to { maxPerQuestion: 20 }')
    ;(raw as Record<string, unknown>).highlightsConfig = { maxPerQuestion: 20 }
  }

  if (raw.seasonSubjectAvailability !== undefined) {
    if (!isObject(raw.seasonSubjectAvailability)) {
      console.warn('[config] seasonSubjectAvailability must be an object — ignoring')
      delete (raw as Record<string, unknown>).seasonSubjectAvailability
    } else {
      const subjectKeys = new Set((raw.subjects as Array<{ key: string }>).map((s) => s.key))
      for (const [season, subjects] of Object.entries(raw.seasonSubjectAvailability)) {
        if (!Array.isArray(subjects) || !subjects.every((s) => typeof s === 'string')) {
          console.warn(`[config] seasonSubjectAvailability.${season} must be an array of strings — ignoring entry`)
          delete (raw.seasonSubjectAvailability as Record<string, unknown>)[season]
          continue
        }
        for (const subjectKey of subjects) {
          if (!subjectKeys.has(subjectKey)) {
            console.warn(`[config] seasonSubjectAvailability.${season} references unknown subject: ${subjectKey}`)
          }
        }
      }
    }
  }

  return { ok: true, config: raw as unknown as ClientConfig }
}
